import { useState } from 'react'

const SENTIMENT_API = "https://sentiment-analyzer.tamilframework.workers.dev"

function App() {
  const [postUrl, setPostUrl] = useState("")
  const [commentsText, setCommentsText] = useState("")
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState(null)
  const [error, setError] = useState(null)

  const analyzeComments = async () => {
    if (!commentsText.trim()) {
      setError("Please paste comments")
      return
    }

    setLoading(true)
    setError(null)

    try {
      const lines = commentsText.split("\n").filter(l => l.trim())
      const comments = lines.map(line => {
        const parts = line.split(":")
        if (parts.length >= 2) {
          return { username: parts[0].trim().replace("@", ""), text: parts.slice(1).join(":").trim() }
        }
        return { username: "anonymous", text: line.trim() }
      }).filter(c => c.text)

      if (comments.length === 0) {
        setError("No valid comments found")
        setLoading(false)
        return
      }

      const response = await fetch(SENTIMENT_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ comments, post_url: postUrl })
      })

      const data = await response.json()

      setResults({
        post: { url: postUrl },
        total_comments: comments.length,
        analysis: data
      })

    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const startOver = () => {
    setPostUrl("")
    setCommentsText("")
    setResults(null)
    setError(null)
  }

  return (
    <div className="app">
      <header className="header">
        <div className="header-logo">T</div>
        <div className="header-title">TN Politics Sentiment</div>
      </header>

      <main className="main">
        {!results ? (
          <div className="card">
            <div className="card-title">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="#5f6368"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>
              Instagram Comment Analysis
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ fontSize: "12px", color: "#5f6368", display: "block", marginBottom: "4px" }}>Post URL (optional)</label>
              <input
                type="text"
                className="url-input"
                placeholder="https://www.instagram.com/p/..."
                value={postUrl}
                onChange={(e) => setPostUrl(e.target.value)}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ fontSize: "12px", color: "#5f6368", display: "block", marginBottom: "4px" }}>Comments (one per line, format: username: comment)</label>
              <textarea
                className="comments-textarea"
                placeholder={`user1: Great post! Love this leader\nuser2: Best musician ever\nuser3: Terrible, hate this\nuser4: Nice work, keep it up`}
                value={commentsText}
                onChange={(e) => setCommentsText(e.target.value)}
              />
            </div>

            <p style={{ fontSize: "12px", color: "#9aa0a6", marginBottom: "16px" }}>
              Open the Instagram post in your browser, copy comments, and paste them above
            </p>

            <button className="btn-primary" onClick={analyzeComments} disabled={loading}>
              {loading ? "Analyzing..." : "Analyze Sentiment"}
            </button>

            {error && <div className="error">{error}</div>}
          </div>
        ) : (
          <>
            <div className="card">
              <div className="card-title">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="#5f6368"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/></svg>
                Results
              </div>

              <div className="sentiment-summary">
                <div className="sentiment-card total">
                  <div className="sentiment-value">{results.total_comments}</div>
                  <div className="sentiment-label">Total</div>
                </div>
                <div className="sentiment-card positive">
                  <div className="sentiment-value">{results.analysis.summary?.positive_count || 0}</div>
                  <div className="sentiment-label">Positive {results.analysis.summary?.positive_pct || 0}%</div>
                </div>
                <div className="sentiment-card negative">
                  <div className="sentiment-value">{results.analysis.summary?.negative_count || 0}</div>
                  <div className="sentiment-label">Negative {results.analysis.summary?.negative_pct || 0}%</div>
                </div>
                <div className="sentiment-card neutral">
                  <div className="sentiment-value">{results.analysis.summary?.neutral_count || 0}</div>
                  <div className="sentiment-label">Neutral {results.analysis.summary?.neutral_pct || 0}%</div>
                </div>
              </div>

              <div className="progress-bar">
                <div className="positive" style={{ width: `${results.analysis.summary?.positive_pct || 0}%` }}></div>
                <div className="negative" style={{ width: `${results.analysis.summary?.negative_pct || 0}%` }}></div>
                <div className="neutral" style={{ width: `${results.analysis.summary?.neutral_pct || 0}%` }}></div>
              </div>

              <div className="insights-box">
                <div className="insights-title">Key Insights</div>
                <div className="insights-text">{results.analysis.insights}</div>
              </div>

              <div className="comments-header">
                Comments ({results.analysis.comments?.length || 0})
              </div>
              <div className="comments-list">
                {results.analysis.comments?.map((comment, index) => (
                  <div key={index} className="comment-item">
                    <div className="comment-avatar">{comment.username?.[0]?.toUpperCase() || "?"}</div>
                    <div className="comment-content">
                      <div className="comment-username">@{comment.username}</div>
                      <div className="comment-text">{comment.text}</div>
                    </div>
                    <span className={`comment-badge ${comment.sentiment}`}>
                      {comment.sentiment}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ textAlign: "center", marginTop: "24px" }}>
              <button className="btn-primary" onClick={startOver}>
                Analyze Another Post
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  )
}

export default App